import React, { useEffect, createContext, useContext, useState, useRef, useCallback, useMemo } from "react";
import { addEvolution as addEvolutionAction, onChangeEvent as checkInOnChangeEvent, getRoomOccupationData, addSubscriptionDevice, getBalancePaymentUrl as getBalancePaymentUrlAction, getBalance as getBalanceAction, addEvolution } from '../actions/checkInActions';
import { onChangeEvent as requestsOnChangeEvent } from "../actions/requestsActions";
import useCheckInActions from "../hooks/useCheckInActions";
import { HotelContext } from "./hotel";
import { UserContext } from "./user";
import { usePersistentState } from '../hooks/usePersistentState';
import { useLanguage } from "../hooks/useLanguage";
import { subscribe, unsubscribe } from "../events";
import { encodeBase64 } from '../functions';
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { getCDNImage } from "../globals";
// import { unlock } from '../actions/smartLocksActions';
// import { useWebPush } from "../hooks/useWebPush";
import { useSessionPersistence } from '../hooks/useSessionPersistence';
import useStayActions from "../hooks/useStayActions";
import { v4 as uuidv4 } from 'uuid';
import { isBlackSummit, isHostzBetaHouse, isHostzBlackSummit, isHostzSteiners, isSimplex, isGemunder, isHostzSwissstay, isNewsletterHotel } from "../components/CheckIn/hotelConditions";
// import useEventSuscribers from "../hooks/useEventSubscribers";
import { useKiosk } from "../hooks/useKiosk";
import { API, graphqlOperation } from "aws-amplify";
import { normalizePassengers } from "../helpers/normalizePassengerGender";

const checkin_onCreateEventMark = /* GraphQL */ `
  subscription OnCreateEventMark($checkInId: String!) {
    onCreateEventMark(filter: {
        entity: {
            eq: "checkin"
        },
        entityId: {
            eq: $checkInId
        }
    }) {
        entity
        entityId
        topic
    }
  }
`;

const CheckInContext = createContext({});

const checkInSchema = Yup.object().shape({
    passengers: Yup.array().of(Yup.object({
        name: Yup.string().when('ommited', (ommited, schema) => {
            if (ommited) return schema;
            return schema.required('yup.required');
        }),
        // Black Summit exige los datos de todos los acompañantes, no solo el nombre
        surname: Yup.string().nullable().when(['ommited', '$hotelId'], (ommited, hotelId, schema) => {
            if (ommited || !isBlackSummit(hotelId)) return schema;
            return schema.required('yup.required');
        }),
        birthday: Yup.string().nullable().when(['ommited', '$hotelId'], (ommited, hotelId, schema) => {
            if (ommited || !isBlackSummit(hotelId)) return schema;
            return schema.required('yup.required');
        }),
        nationality: Yup.string().nullable().when(['ommited', '$hotelId'], (ommited, hotelId, schema) => {
            if (ommited || !isBlackSummit(hotelId)) return schema;
            return schema.required('yup.required');
        })
    })).required(),
    email: Yup.string().nullable()
        .email("yup.email-invalid-format")
        .required('yup.required'),
    phone: Yup.string().min(7, 'yup.minimal-7-digits').max(25, 'yup.maximal-25-digits')
        .nullable().required('yup.required'),
    sign: Yup.string().nullable().required('yup.required'),
    travelType: Yup.string()
        .when(["$hotelId"], {
            is: (hotelId) => !isHostzBlackSummit(hotelId) && !isHostzBetaHouse(hotelId) && !isHostzSteiners(hotelId) && !isHostzSwissstay(hotelId),
            then: Yup.string().required("yup.required").nullable(),
            otherwise: Yup.string().nullable()
        }),

    entryTime: Yup.string()
        .when(["$hotelId"], {
            is: (hotelId) => isHostzBlackSummit(hotelId),
            then: Yup.string().required("yup.required").nullable(),
            otherwise: Yup.string().nullable()
        }),
    arrivalMode: Yup.string()
        .when(["$hotelId"], {
            is: (hotelId) => isHostzBlackSummit(hotelId),
            then: Yup.string().required("yup.required").nullable(),
            otherwise: Yup.string().nullable()
        }),
    extraData: Yup.object({
        qtyAdults: Yup.string()
            .when(["$hotelId"], {
                is: (hotelId) => isHostzBlackSummit(hotelId),
                then: Yup.string().required("yup.required").nullable(),
                otherwise: Yup.string().nullable()
            }),
        qtyChild: Yup.string()
            .when(["$hotelId"], {
                is: (hotelId) => isHostzBlackSummit(hotelId),
                then: Yup.string().required("yup.required").nullable(),
                otherwise: Yup.string().nullable()
            }),
        qtyBabies: Yup.string()
            .when(["$hotelId"], {
                is: (hotelId) => isHostzBlackSummit(hotelId),
                then: Yup.string().required("yup.required").nullable(),
                otherwise: Yup.string().nullable()
            })
    }),

    linked_checkins: Yup.array().of(Yup.object({
        passengers: Yup.array().of(Yup.object({
            dni: Yup.string().nullable().when('ommited', (ommited, schema) => {
                if (ommited) return schema;
                return schema.required('yup.required');
            }),
            surname: Yup.string().nullable().when('ommited', (ommited, schema) => {
                if (ommited) return schema;
                return schema.required('yup.required');
            }),
            dni: Yup.string().nullable().when('ommited', (ommited, schema) => {
                if (ommited) return schema;
                return schema.required('yup.required');
            })
        })).required()
    }))
});

function CheckInProvider({ children }) {
    const [lastEvent, setLastEvent] = useState(null);
    const [eventVersion, setEventVersion] = useState(0);

    const [data, setData, setPartialData] = usePersistentState("checkin-current", null, 72000)
    const { data: hotel, getUrlPath } = useContext(HotelContext)
    const { stayClear: stayClearInUser, setStayForceInit, stayCheckIn, inStay: userInStay, stayCheckOut, setCheckIn, syncAccessContextFromStatus, language, shortLanguage, WebPush, setShowRequestPermissionModal, getNotificationObject, requests, getRequests } = useContext(UserContext)
    const { identify: identifyAction, identifyByData: identifyByDataAction, refreshStatus: refreshStatusAction, saveCheckIn: saveAction, addComment: addCommentAction } = useCheckInActions();
    // const { onRequestEvent } = useEventSuscribers(data?.id);
    const [sendingComment, setSendingComment] = useState(false)
    const [sendingEvolution, setSendingEvolution] = useState(false)
    const [refreshingStatus, setRefreshingStatus] = useState(false)

    const onCheckInChangeSubscription = useRef(null)
    const subscriptionRef = useRef(null);
    /** Id del check-in activo: evita que refresh/stayCheckIn de una reserva anterior contaminen UserContext. */
    const currentCheckInIdRef = useRef(data?.id || null);
    // const onRequestsChangeSubscription = useRef(null)

    const [errors, setErrors] = useState({});
    const onPassengersPhotosEventsCallback = useRef(null)
    const navigate = useNavigate();

    const { sessionId } = useSessionPersistence();
    const { getStaybyId } = useStayActions();
    const { isKioskMode } = useKiosk();

    useEffect(() => {
        if (data?.id) {
            currentCheckInIdRef.current = data.id;
        }
    }, [data?.id]);
    // useEffect(() => {
    //     if (!data?.id) return;

    //     const subscription = API.graphql(graphqlOperation(checkin_onCreateEventMark, {
    //         "checkInId": data.id
    //     })).subscribe({
    //         next: ({ value }) => {
    //             const newEvent = value?.data?.onCreateEventMark;
    //             console.log("📡 Nuevo check event:", newEvent);
    //             setLastEvent(newEvent);
    //             setEventVersion((v) => v + 1); // 🔥 incrementa en cada evento
    //         },
    //         error: (err) => console.error("❌ Error en subscription:", err),
    //     });
    //     return () => subscription.unsubscribe();
    // }, [ data?.id ]);

    useEffect(() => {
        if (!data?.id) return;

        let retryDelay = 1000; // 1s inicial
        let active = true;

        function startSubscription() {
            if (!active) return;

            subscriptionRef.current = API.graphql(
                graphqlOperation(checkin_onCreateEventMark, { checkInId: data.id })
                ).subscribe({
                    next: ({ value }) => {
                        retryDelay = 1000; // reset del backoff cuando llega algo
                        const newEvent = value?.data?.onCreateEventMark;
                        setLastEvent(newEvent);
                        setEventVersion((v) => v + 1);
                    },
                    error: (err) => {
                        console.error("❌ Error en subscription:", err);
                        resubscribe();
                    },
                    complete: () => {
                        resubscribe();
                    },
                });
        }


        function resubscribe() {
            if (!active) return;
            setTimeout(() => {
                retryDelay = Math.min(retryDelay * 2, 30000); // backoff exponencial máx 30s
                startSubscription();
            }, retryDelay);
        }

        // inicia
        startSubscription();

        // cleanup
        return () => {
            active = false;
            subscriptionRef.current?.unsubscribe();
        };
    }, [data?.id]);


    useEffect(() => {
        refreshStatus()
    }, [ eventVersion ]);

    useEffect(() => {
        if (!sessionId) return;
        if (data?.id) return;

        const _arr = sessionId.split('_');
        const checkInId = _arr[0];
        const reservationCode = _arr[1];
        // alert(reservationCode)
        if (reservationCode) {
            setStayForceInit(true)
            // alert(identify)
            if (identify) {
                identify(reservationCode)
                    .then(() => {
                        navigate(getUrlPath('/home'), { replace: true })
                    })
                    .catch(err => {
                        console.error('0712 - err', err.message)
                    })
            }
        }
    }, [sessionId])

    // TODO::: Ver de reubicar... si tengo checkin identificado pero no push aceptado mostrar el ASK
    // useEffect(() => {
    //     if (!data?.id) return;

    //     setTimeout(() => {
    //         const notif = getNotificationObject();
    //         console.log('1112 - notif', notif)
    //         if (notif) {
    //             console.log('1112 - notif.permission', notif.permission)
    //             alert(notif.permission)
    //             if (notif.permission === "default") {
    //                 setShowRequestPermissionModal(true);
    //             }
    //         }
    //     }, 5000);

    // }, [ data ])



    const identify = useCallback(async (code) => {

        const reservation = await identifyAction(code);

        if (reservation.id !== data?.id) {
            stayClear()
            // parche ----
            await new Promise(resolve => setTimeout(resolve, 1500));
            // parche ----
        }
        if (reservation.email && !isMailAllowed(reservation.email)) {
            reservation.email = '';
        }

        if(isGemunder(hotel?.id) && reservation?.passengers && reservation?.passengers?.length > 1) {
            reservation?.passengers?.forEach((p, idx) => {
                if(idx > 0 && !p.ommited) {
                    p.ommited = true;
                }
            });
        }

        currentCheckInIdRef.current = reservation.id;
        setData(reservation);
        setCheckIn(reservation)

        // -- Parche Forzar get RoomOccupation
        console.log('28ene - parche - reservation.roomOccupationId', reservation.roomOccupationId)
        if (reservation.roomOccupationId) {
            console.log('28ene - getStaybyId')
            getStaybyId(reservation.roomOccupationId).then(rRO => {
                if (reservation.id !== currentCheckInIdRef.current) return;
                console.log('28ene - stayChackIn', rRO, reservation)
                stayCheckIn(rRO, reservation)
            })
        }
        // -- Parche ---

        refreshStatus(reservation.id);


        return reservation;
    }, [data]);

    const identifyByData = useCallback(async (searchData) => {
        const reservation = await identifyByDataAction(encodeBase64(JSON.stringify(searchData)));
        // Comparar contra el check-in del contexto (data), no contra el payload de búsqueda (searchData).
        if (reservation.id !== data?.id) {
            stayClear()
            // parche ----
            await new Promise(resolve => setTimeout(resolve, 1500));
            // parche ----
        }
        if (reservation.email && !isMailAllowed(reservation.email)) {
            reservation.email = '';
        }
        currentCheckInIdRef.current = reservation.id;
        setData(reservation);

        setCheckIn(reservation)

        // -- Parche Forzar get RoomOccupation
        if (reservation.roomOccupationId) {
            getStaybyId(reservation.roomOccupationId).then(rRO => {
                if (reservation.id !== currentCheckInIdRef.current) return;
                stayCheckIn(rRO, reservation)
            })
        }
        // -- Parche ---

        refreshStatus(reservation.id);


        return reservation;
    }, [data]);



    const stayClear = () => {
        console.log('28ene - stayClearInUser')
        currentCheckInIdRef.current = null;
        stayClearInUser();
        if (onCheckInChangeSubscription.current?.subscription) {
            onCheckInChangeSubscription.current.subscription.unsubscribe()
        }
        onCheckInChangeSubscription.current = null;
        // if (onRequestsChangeSubscription.current?.subscription) {
        //     onRequestsChangeSubscription.current.subscription.unsubscribe()
        // }
        // onRequestsChangeSubscription.current = null;
        setData({});
    }

    const resetErrors = () => {
        setErrors({})
    }

    const refreshStatus = useCallback(async (checkInId) => {
        const targetId = checkInId || currentCheckInIdRef.current || data?.id;
        if (!targetId) return;

        setRefreshingStatus(true);
        
        const r = await refreshStatusAction(hotel.id, targetId);
        // Ignorar respuesta de una reserva que ya no es la activa (p.ej. identify cambió de B → A).
        if (r?.id && r.id !== currentCheckInIdRef.current) {
            setRefreshingStatus(false);
            return r;
        }
        if (r?.id === targetId) {
            // setData({ ...data, roomOccupationId: r.roomOccupationId, status: r.status, evolutions: r.evolutions })
            console.log('24oct - refreshStatusAction - r', r);
            const payload = {
                roomOccupationId: r.roomOccupationId,
                status: r.status,
                webCheckInDone: r.webCheckInDone,
                evolutions: r.evolutions,
                showNotification: r.showNotification
            }
            if (r.Room !== undefined) payload.Room = r.Room;
            if (r.RoomCategory !== undefined) payload.RoomCategory = r.RoomCategory;
            if (r.reservation !== undefined) payload.reservation = r.reservation;
            if (r.placeholders !== undefined) payload.placeholders = r.placeholders;
            if (r.Room) {
                syncAccessContextFromStatus({
                    Room: r.Room,
                    RoomCategory: r.RoomCategory,
                    reservation: r.reservation,
                    placeholders: r.placeholders,
                });
            }
            if (r.status === "in_stay" && !data?.inStay) {
                payload["inStay"] = true;

                getStaybyId(r.roomOccupationId).then(rRO => {
                    if (r.id !== currentCheckInIdRef.current) return;
                    stayCheckIn(rRO, r)
                })
            }

            setPartialData(payload);

            if (r.status !== "pending" && !isKioskMode) {
                getRequests();
            }
        }

        setRefreshingStatus(false)

        return r;

    }, [data]);

    useEffect(() => {
        const paramSessionId = data?.id && data?.externalId ? data.id + "_" + data.externalId : null;
        generateManifiest(hotel, data, paramSessionId)
        // if ('Notification' in window && Notification.permission === 'default' && data?.id) {
        //     setShowRequestPermission('accept');
        // }
        return false;
    }, [data])

    const addComment = (comment) => {
        setSendingComment(true);
        return addCommentAction(data.id, comment)
            .then(r => {
                refreshStatus();
                return r;
            })
            .finally(() => setSendingComment(false))
    }

    const addEvolution = (status, comment = "") => {
        setSendingEvolution(true);
        return addEvolutionAction(hotel.id, data.id, status, comment)
            .then(r => {
                refreshStatus();
                return r;
            })
            .finally(() => setSendingEvolution(false))
    }

    const setPassengerData = useCallback((idx, values) => {
        const _passengers = [...data.passengers];
        if (idx !== null) {
            _passengers[idx] = { ..._passengers[idx], ...values };
        }
        setData({ ...data, passengers: [..._passengers] });
    }, [data]);

    const setPassengerOmmit = useCallback((idx, value) => {
        const _passengers = [...data.passengers];
        if (idx !== null) {
            _passengers[idx] = { ..._passengers[idx], ommited: value };
        }
        setData({ ...data, passengers: [..._passengers] });
    }, [data]);

    const setPassengerLinkedCheckinOmmit = useCallback((linkedCheckinIdx, idx, value) => {
        const _linked_checkins = [...data.linked_checkins];
        console.log('28dic - _linked_checkins', _linked_checkins)
        const _passengers = [..._linked_checkins[linkedCheckinIdx].passengers];
        console.log('28dic - _passengers', _passengers)
        if (idx !== null) {
            _passengers[idx] = { ..._passengers[idx], ommited: value };
        }
        _linked_checkins[linkedCheckinIdx].passengers = _passengers
        console.log('28dic - _linked_checkins', _linked_checkins)
        setData({ ...data, linked_checkins: [..._linked_checkins] });
    }, [data]);

    const setPassengerLinkedCheckinData = useCallback((linkedCheckinIdx, idx, values) => {
        const _linked_checkins = [...data.linked_checkins];
        console.log('28dic - _linked_checkins', _linked_checkins)
        const _passengers = [..._linked_checkins[linkedCheckinIdx].passengers];
        console.log('28dic - _passengers', _passengers)
        if (idx !== null) {
            _passengers[idx] = { ..._passengers[idx], ...values };
        }
        _linked_checkins[linkedCheckinIdx].passengers = _passengers
        console.log('28dic - _linked_checkins', _linked_checkins)
        setData({ ...data, linked_checkins: [..._linked_checkins] });
    }, [data]);

    // const uploadPassengerImage = (idx, code, value) => {
    //     console.log('idx', idx)
    //     console.log('code', code)
    //     console.log('value', value)

    //     // setPassengersPhotos({ ...passengersPhotos, [code]: { ...passengersPhotos[code], uploading: true } });
    //     console.log('onPassengersPhotosEventsCallback.current', onPassengersPhotosEventsCallback.current)
    //     if (onPassengersPhotosEventsCallback?.current) {
    //         onPassengersPhotosEventsCallback.current.call(this, { idx: idx, code: code, event: 'uploading', value: true })
    //     }
    //     var fileName = data.id + '_' + code + '_' + idx + '_' + Date.now() + ".jpg";

    //     Storage.put(fileName, value).then(res => {
    //       if(res && res.key !== undefined){
    //         // setPassengersPhotos({ ...passengersPhotos, [code]: { ...passengersPhotos[code], uploading: false, value: res.key } });
    //         if (onPassengersPhotosEventsCallback?.current) {
    //             onPassengersPhotosEventsCallback.current.call(this, { idx: idx, code: code, event: 'uploaded', value: res.key })
    //         }
    //         // setUploadingSelfie(false);
    //         // setSelfieToUpload(null);
    //         // setValues({...values, photos : { ...values.photos, selfie : res.key }});
    //         // setSelfie(res.key);
    //       }
    //     });
    // }

    const validateAt = useCallback((path) => {
        let _valid = false;
        try {
            _valid = checkInSchema.validateSyncAt(path, data, { context: { hotelId: hotel?.id } });
        } catch (err) {
            _valid = false;
            console.log('err', err)
            console.log('err.name', err.name)
        }
        return _valid;
        // return new Promise((resolve, reject) => {
        //     checkInSchema.validateAt(path, data)
        //         .then(() => {
        //             console.log('OK');
        //             resolve()
        //         }).catch(function (err) {
        //             const validationErrors = {};
        //             console.log('err', err)
        //             err.inner?.forEach(error => {
        //             if (error.path) {
        //                 validationErrors[error.path.replace('.', '_')] = error.message;
        //             }
        //             });
        //             console.log('validationErrors', validationErrors)
        //             setErrors(validationErrors)
        //             reject(validationErrors)
        //         })
        // });
    }, [data, hotel?.id]);

    const validate = useCallback(() => {
        try {
            const r = checkInSchema.validateSync(data, { context: { hotelId: hotel?.id }, abortEarly: false })
        } catch (error) {
            const validationErrors = {};
            error.inner?.forEach(error => {
                if (error.path) {
                    validationErrors[error.path.replace('.', '_')] = error.message;
                }
            });
            setErrors(validationErrors)
        }
    }, [data, hotel?.id])




    const prepareData = useCallback((data, language) => {

        const acceptedKeys = [
            "id", "hotelId", "origin", "code", "externalId", "securityCode", "reservation",
            "channel", "channelCode", "userId", "language", "dateStart", "dateEnd",
            "entryTime", "passengers", "travelType", "arrivalMode", "status",
            "roomId", "roomCategoryId", "roomOccupationId", "email", "phone",
            "invitationEmailStatus", "invitationEmail2ndStatus", "invitationEmail3rdStatus",
            "infoEmailStatus", "smartLockPinCode", "smartLockPinCodeDetail",
            "smartLockPinCodeEmailStatus", "subscriptions", "pricePerDay", "extraData",
            "unread", "showNotification", "sign", "balance", "reviewId", "crossUpSelling",
            "createdAt", "updatedAt", "linked_checkins", "newsletter"
        ];

        const preparedData = Object.keys(data).reduce((acc, key) => {
            if (acceptedKeys.includes(key)) {
                acc[key] = data[key];
            }
            return acc;
        }, {});

        preparedData.language = language;
        preparedData.showNotification = 1;
        preparedData.extraData = JSON.stringify(preparedData.extraData);
        preparedData.reservation && (preparedData.reservation = JSON.stringify(preparedData.reservation));

        const acceptedCrossUpSellingKeys = [
            "id", "key", "provider", "code", "title", "titleI18n", "optionId",
            "description", "descriptionI18n", "price", "photo", "availabilityStatus", "requestId", "extra", "type"
        ]

        if (preparedData.crossUpSelling) {
            preparedData.crossUpSelling = preparedData.crossUpSelling.map(cups => {
                cups.provider = "own";
                cups.extra = cups.extra && typeof cups.extra === "object" ? cups.extra : {};
                if (typeof cups.titleI18n !== "string") {
                    cups.titleI18n = JSON.stringify(cups.titleI18n);
                }
                if (typeof cups.descriptionI18n !== "string") {
                    cups.descriptionI18n = JSON.stringify(cups.descriptionI18n);
                }
                if (typeof cups.bookData !== "string") {
                    if (cups.bookData && cups.bookData.hour && cups.bookData.hour.endsWith(":00")) {
                        cups.bookData.hour = cups.bookData.hour.slice(0, 2);
                    }
                    cups.extra = { ...cups.extra, ...cups.bookData };
                }
                if (cups.optionSelected?.id) {
                    cups["optionId"] = cups.optionSelected.id;
                }
                if (cups.optionSelected?.price != null) {
                    cups.price = cups.optionSelected.price;
                }
                if (cups.optionSelected?.type === "items") {
                    cups.type = "items";
                    if (cups.optionSelected.items && cups.optionSelected.items.length > 0) {
                        cups.extra.items = cups.optionSelected.items;
                    }
                }
                if (cups.settingData) {
                    cups.extra = { ...cups.extra, ...cups.settingData };
                }
                console.log('11feb - cups', cups)
                console.log('11feb - cups.extra', cups.extra)
                console.log('11feb - cups.settingData', cups.settingData)
                cups.extra = JSON.stringify(cups.extra);
                return Object.keys(cups).reduce((acc, key) => {
                    if (acceptedCrossUpSellingKeys.includes(key)) {
                        acc[key] = cups[key];
                    }
                    return acc;
                }, {});
            });
        }
        if (preparedData.passengers) {
            preparedData.passengers = normalizePassengers(preparedData.passengers);
        }
        if (preparedData.linked_checkins) {
            preparedData.linked_checkins = preparedData.linked_checkins.map((linkedCheckin) => {
                if (!linkedCheckin?.passengers) return linkedCheckin;
                return {
                    ...linkedCheckin,
                    passengers: normalizePassengers(linkedCheckin.passengers),
                };
            });
        }
        console.log('26ene - preparedData', preparedData)
        return preparedData
    }, [data]);


    const sendToValidate = useCallback(() => {

        const preparedData = prepareData(data, language);
        console.log('26ene - preparedData', preparedData)

        let _firstComment = false;
        if (data.status === 'pending' && data.comment) {
            _firstComment = data.comment;
        }


        // TODO:::: parche para no mandar los crossUpSelling cuando es edicion
        // Ver de hacer en el backend tb el control o hacerlo de otra manera
        if (data.webCheckInDone) {
            delete data.crossUpSelling;
        }
        // 

        // setPartialData({ showAppreciationCheckinComplete : true })

        return new Promise((resolve, reject) => {
            setErrors({});
            console.log('30dic - preparedData', preparedData)

            checkInSchema.validate(preparedData, { context: { hotelId: hotel.id }, abortEarly: false })
                .then(function () {

                    console.log('30dic - si')

                    if (!preparedData.travelType && preparedData.travelType !== null) {
                        preparedData.travelType = null;
                    }
                    if (preparedData.status === 'pending' || preparedData.status === 'observed') {
                        preparedData.status = "to_validate";
                    }

                    let evolutionStatus = preparedData.status;
                    if (preparedData.status === "in_stay") {
                        evolutionStatus = "to_validate";
                    }

                    // -----
                    delete (preparedData.status);
                    // -----

                    console.log('11feb - preparedData', preparedData)

                    saveAction(preparedData).then(async r => {
                        if (r.status === "error") {
                            alert("error")
                            return;
                        }
                        console.log('31oct - r', r)
                        if (r.status === "ok") {
                            if (r.data?.status && r.data.status !== data.status) {
                                setPartialData({ status: r.data.status });
                            } else {
                                setPartialData({ status: "to_validate" });
                            }
                            if (_firstComment) {
                                addEvolution(evolutionStatus, _firstComment ? _firstComment : "");
                            }

                            resolve(r.data);
                        }

                    }).catch(err => {
                        console.error(err)
                        reject(err)
                    });
                }).catch(function (err) {
                    console.log('30dic - err', err)
                    const validationErrors = {};
                    err.inner?.forEach(error => {
                        if (error.path) {
                            validationErrors[error.path.replace('.', '_')] = error.message;
                        }
                    });
                    setErrors(validationErrors);
                    reject(validationErrors);
                });
        });
    }, [data, language, prepareData]);


    const setNewsletter = useCallback(value => {
        setData({ ...data, newsletter: value })
    }, [data])

    const setTravelType = useCallback(value => {
        setData({ ...data, travelType: value })
    }, [data])

    const setEmail = useCallback(value => {
        setData({ ...data, email: value })
    }, [data])

    const setPhone = useCallback(value => {
        setData({ ...data, phone: value })
    }, [data])

    const setComment = useCallback(value => {
        setData({ ...data, comment: value })
    }, [data])

    const setEntryTime = useCallback(value => {
        setData({ ...data, entryTime: value })
    }, [data])

    const setArrivalMode = useCallback(value => {
        setData({ ...data, arrivalMode: value })
    }, [data])

    const setExtraData = useCallback((key, value) => {
        const _extraData = data.extraData ?? {};
        _extraData[key] = value;
        setData({ ...data, extraData: _extraData })
    }, [data])

    const setSign = useCallback(value => {
        setData({ ...data, sign: value })
    }, [data])

    const setCrossUpSellingItems = useCallback(value => {
        value.map(c => {
            c['key'] = uuidv4();
            return c;
        })
        setData(prevData => ({
            ...prevData,
            crossUpSelling: value
        }));
    }, []);

    const getBalancePaymentUrl = useCallback(() => {
        return getBalancePaymentUrlAction(hotel.id, data.id)
    }, [data])

    const getBalance = useCallback(() => {
        if (data?.id) return Promise.resolve(false);
        return getBalanceAction(data.id)
    }, [data])




    // const save = (values) => {
    //     const _toSave = { 
    //         id: values.id || null,
    //         dateStart: values.dateStart,
    //         dateEnd: values.dateEnd,
    //         language: language,
    //         travelType: values.travelType,
    //         passengers: values.passengers
    //     }
    //     if (!_toSave.id) {
    //         _toSave.hotelId = hotel.id;
    //         _toSave.origin = 'pwa'
    //         _toSave.status = "pending"
    //     } else {
    //         if (data.status == 'observed') {
    //             _toSave.status = "fixed"
    //         }
    //     }
    //     console.log('_toSave', _toSave)
    //     return chekinSchema.validate(_toSave, { abortEarly: false }).then(function () {
    //         console.log('aaaaaa')
    //         saveAction(_toSave).then(r => {
    //             setData({ ...data, ..._toSave })
    //             alert('go detail')
    //             // goDetail()
    //         })
    //         // dispatch(saveCheckIn(data))
    //         // setEditMode(false);
    //       }).catch(function (err) {
    //         console.log('ERR', err)
    //         const validationErrors = {};
    //         err.inner.forEach(error => {
    //           if (error.path) {
    //             validationErrors[error.path] = error.message;
    //           }
    //         });
    //         console.log('validationErrors', validationErrors)
    //         throw Error(JSON.stringify(validationErrors));
    //     });
    // }

    // const goEdit = useCallback(() => {
    //     history.replace(`/${hotel.code}/checkin/edit`)
    // }, [data?.code])

    // const goDetail = useCallback(() => {
    //     history.replace(`/${hotel.code}/checkin`)
    // }, [data?.code])

    useEffect(() => {
        if (!data) return
        if (!refreshingStatus) {
            refreshStatus();
        }
    }, [data?.id])

    // TODO::::: REVISAR
    // useEffect(() => {
    //     if (!data?.id) return
    //     console.log('28ene - useeffect - data', data)
    //     console.log('28ene - useeffect - userInStay', userInStay)
    //     if (data.status === 'in_stay' && data.roomOccupationId && !userInStay) {
    //         console.log('28ene - getRoomOccupationData')
    //         getRoomOccupationData(data.roomOccupationId).then(r => {
    //             console.log('28ene - setData', { ...data, inStay: true })
    //             setData({ ...data, inStay: true })
    //             console.log('28ene - stayCheckIn', r)
    //             stayCheckIn(r)
    //         })
    //     }
    //     if (data.status === 'done' && data.roomOccupationId && userInStay) {
    //         setData({ ...data, inStay: false })
    //         stayCheckOut()
    //     }
    // }, [data])
    // TODO::::: REVISAR

    React.useEffect(() => {
        if (!data?.id) return;

        if (onCheckInChangeSubscription.current?.id && data.id !== onCheckInChangeSubscription.current?.id) {
            onCheckInChangeSubscription.current.subscription.unsubscribe()
            onCheckInChangeSubscription.current = null;
        }
        if (!onCheckInChangeSubscription.current) {
            onCheckInChangeSubscription.current = {
                id: data.id,
                subscription: checkInOnChangeEvent(data.id, r => {
                    console.log('16ago - events - onchange - r', r);
                    if (r.id === onCheckInChangeSubscription.current.id) {
                        let payload = {
                            status: r.status,
                            roomOccupationId: data?.roomOccupationId || r.roomOccupationId
                        }
                        if (r.status === "in_stay") {
                            payload["inStay"] = true;
                        }
                        updateData(payload);
                    }
                })
            }
        }

    }, [data?.id])

    const updateData = useCallback(newData => {

        // setData({ ...data, ...newData })
        setPartialData(newData)

        // console.log('refreshStatus 3', data)
        refreshStatus();
    }, [data]);

    useEffect(() => {
        return () => {
            if (onCheckInChangeSubscription.current) {
                try {
                    if (onCheckInChangeSubscription.current?.subscription?.unsubscribe) {
                        onCheckInChangeSubscription.current.subscription.unsubscribe();
                    }
                } finally {
                    onCheckInChangeSubscription.current = null;
                }
            }
        }
    }, [])

    const addPassengersPhotosEventsCallback = (cb) => {
        onPassengersPhotosEventsCallback.current = cb;
    }

    const generateManifiest = (hotel, checkIn, sessionId = null) => {

        let _host = "https://hotel.vicomanager.com";
        if (process.env.REACT_APP_APP_ENV === 'local') {
            _host = window.location.protocol + '//' + window.location.host;
        }
        if (process.env.REACT_APP_APP_ENV === 'dev') {
            _host = "https://hotel-dev.vicomanager.com";
        }

        if (hotel.id === "1de880e5-bfb6-4518-bd1b-095443bbc58b") {
            _host = "https://app.hotelcostagalana.com";
        }
        if (hotel.id === "c07d2d8a-9db1-412c-b24d-c0fcc3c5e7a0") {
            _host = "https://app.hotelgrandbrizobelair.com";
        }
        if (hotel.id === "813161e1-a7a7-4916-953b-6dcf80b4c288") {
            _host = "https://app.hotelbrizosalta.com";
        }
        if (hotel.id === "c0bb7b5c-cd44-4288-98f1-4868ea4e5c11") {
            _host = "https://app.hotelgrandbrizocomahue.com";
        }
        if (hotel.id === "f33a83b9-6284-46e6-8469-b5b17de2c9ba") {
            _host = "https://app.hotelbrizoneuquen.com";
        }
        if (hotel.id === "44b7b2ff-e90c-4656-9dcf-96f797b6fdba") {
            _host = "https://app.cgbeach.com.ar";
        }
        if (hotel.id === "2645e6bf-25cc-4d87-8bc4-80b01f026fea") {
            _host = "https://app.hotelunitsantarosa.com";
        }
        if (hotel.id === "57c5ac85-75d3-4f30-9933-bbb12369ce40") {
            _host = "https://app.hotelgrandbrizolaplata.com";
        }
        if (hotel.id === "a15be3ca-5bee-4167-89e2-aaacff0e827e") {
            _host = "https://app.hoteliruna.com";
        }
        if (hotel.id === "fb6185a6-5b1d-4f9a-8db8-60fff4014a6d") {
            _host = "https://app.hotelriviera.com.ar";
        }
        if (hotel.id === "365edffa-c56e-4918-a16a-305452315e8d") {
            _host = "https://app.hotelgrandbrizobuenosaires.com";
        }
        if (hotel.id === "a3c02b2b-fda7-4e13-be6c-72fa342e4a79") {
            _host = "https://app.loscauquenes.com";
        }

        let _staryUrl = _host + '/' + hotel.code + '/home';
        let _origin = _host + "/" + hotel.code;

        if (hotel.id === "1de880e5-bfb6-4518-bd1b-095443bbc58b" ||
            hotel.id === "c07d2d8a-9db1-412c-b24d-c0fcc3c5e7a0" ||
            hotel.id === "813161e1-a7a7-4916-953b-6dcf80b4c288" ||
            hotel.id === "c0bb7b5c-cd44-4288-98f1-4868ea4e5c11" ||
            hotel.id === "f33a83b9-6284-46e6-8469-b5b17de2c9ba" ||
            hotel.id === "44b7b2ff-e90c-4656-9dcf-96f797b6fdba" ||
            hotel.id === "2645e6bf-25cc-4d87-8bc4-80b01f026fea" ||
            hotel.id === "57c5ac85-75d3-4f30-9933-bbb12369ce40" ||
            hotel.id === "a15be3ca-5bee-4167-89e2-aaacff0e827e" ||
            hotel.id === "fb6185a6-5b1d-4f9a-8db8-60fff4014a6d" ||
            hotel.id === "365edffa-c56e-4918-a16a-305452315e8d" ||
            hotel.id === "a3c02b2b-fda7-4e13-be6c-72fa342e4a79") {
            _staryUrl = _host + '/home';
            _origin = _host
        }

        if (sessionId) {
            _staryUrl += '?sessionId=' + sessionId;
        }

        var myDynamicManifest = {
            "id": 'ViCo-Hotel-' + hotel.code,
            "name": hotel.name,
            "short_name": hotel.name, // hotel.code,
            "description": "ViCo Manager for " + hotel.name,
            "start_url": _staryUrl,
            "background_color": "#FFFFFF",
            "display": "standalone",
            "theme_color": "#988460",
            "url_handlers": {
                "origin": _origin
            },
            "icons": [{
                "src": hotel.settings?.icon ? getCDNImage(hotel.settings.icon) : _host + "/maskable_icon_x48.png",
                "sizes": "48x48",
                "type": "image/png",
                "purpose": "maskable"
            }, {
                "src": hotel.settings?.icon ? getCDNImage(hotel.settings.icon) : _host + "/maskable_icon_x72.png",
                "sizes": "72x72",
                "type": "image/png",
                "purpose": "maskable"
            }, {
                "src": hotel.settings?.icon ? getCDNImage(hotel.settings.icon) : _host + "/maskable_icon_x96.png",
                "sizes": "96x96",
                "type": "image/png",
                "purpose": "maskable"
            }, {
                "src": hotel.settings?.icon ? getCDNImage(hotel.settings.icon) : _host + "/maskable_icon_x128.png",
                "sizes": "128x128",
                "type": "image/png",
                "purpose": "maskable"
            }, {
                "src": hotel.settings?.icon ? getCDNImage(hotel.settings.icon) : _host + "/maskable_icon_x144.png",
                "sizes": "144x144",
                "type": "image/png",
                "purpose": "any"
            }, {
                "src": hotel.settings?.icon ? getCDNImage(hotel.settings.icon) : _host + "/maskable_icon_x192.png",
                "sizes": "192x192",
                "type": "image/png",
                "purpose": "maskable"
            }, {
                "src": hotel.settings?.icon ? getCDNImage(hotel.settings.icon) : _host + "/maskable_icon_x384.png",
                "sizes": "384x384",
                "type": "image/png",
                "purpose": "maskable"
            }, {
                "src": hotel.settings?.icon ? getCDNImage(hotel.settings.icon) : _host + "/maskable_icon_x512.png",
                "sizes": "512x512",
                "type": "image/png",
                "purpose": "maskable"
            }]
        };

        const stringManifest = JSON.stringify(myDynamicManifest);
        const blob = new Blob([stringManifest], { type: 'application/json' });
        const manifestURL = URL.createObjectURL(blob);
        document.querySelector('#client-manifest-placeholder').setAttribute('href', manifestURL);
    }

    // useEffect(() => {
    //     subscribe("onPassengerPhoto", (event) => {
    //         console.log('onPassengerPhoto', event.detail);
    //         if (event?.detail) {
    //         console.log('data', event.detail);
    //         const data = event.detail;

    //         let _setState = null;
    //         switch (data.code) {
    //             case 'selfie':
    //             _setState = setPhotoSelfie;
    //             break;
    //         }

    //         setPhotoSelfie({ uploading: true, value: null })

    //         var fileName = checkIn.id + '_' + data.code + '_' + data.idx + '_' + Date.now() + ".jpg";
    //         console.log('---> ', fileName)

    //         Storage.put(fileName, dataURLtoBlob(data.value)).then(res => {
    //             console.log('res', res)
    //             if(res && res.key !== undefined){
    //             // setPassengersPhotos({ ...passengersPhotos, [code]: { ...passengersPhotos[code], uploading: false, value: res.key } });
    //             // if (onPassengersPhotosEventsCallback?.current) {
    //             //     onPassengersPhotosEventsCallback.current.call(this, { idx: idx, code: code, event: 'uploaded', value: res.key })
    //             // }
    //             // setUploadingSelfie(false);
    //             // setSelfieToUpload(null);
    //             // setValues({...values, photos : { ...values.photos, selfie : res.key }});
    //             // setSelfie(res.key);

    //             // publish("onPassengerPhoto", { idx: idx, code: imageCode, event: 'uploaded', value: res.key });

    //             console.log('res', res)
    //             console.log('_setState', _setState)

    //             photoUploaderHandler(res.key);
    //             // _uploader
    //             // setPhotoSelfie({ uploading: false, value: res.key })
    //             }
    //         });

    //         // if (data.event === "uploading") {
    //         //   _setState({ uploading: true, value: null })
    //         //   console.log('sett', { uploading: true, value: null })
    //         // }

    //         // if (data.event === "uploaded") {
    //         //   _setState({ uploading: false, value: data.value })
    //         //   console.log('sett', { uploading: false, value: data.value })
    //         // }

    //         }
    //     });

    //     return () => {
    //         unsubscribe("onPassengerPhoto", () => {
    //           console.log('unsuscribe onPassengerPhoto');
    //         });
    //     }
    // }, []);

    const isMailAllowed = (value) => {
        const invalidSuffixes = ['@guest.booking.com']
        return !invalidSuffixes.some(suffix => value.endsWith(suffix))
    }

    const nightsCount = () => {
        if (!data?.dateStart || !data?.dateEnd) { return 0 }

        const start = new Date(data?.dateStart);
        const end = new Date(data?.dateEnd);
        const diffMs = end - start; // diferencia en milisegundos
        return (diffMs / (1000 * 60 * 60 * 24)); // (1 día = 86400000 ms)
    }

    const value = {
        eventVersion,
        lastEvent,

        hotel,
        data: data || {},
        identify,
        identifyByData,
        addComment,
        sendingComment,
        refreshingStatus,
        evolutions: data?.evolutions || [],
        sendToValidate,
        inStay: data?.status === 'in_stay' && data?.roomOccupation?.id,
        webCheckInDone: data?.webCheckInDone || false,
        stayClear,
        passengers: data?.passengers || [],
        linked_checkins: data?.linked_checkins || [],
        addPassengersPhotosEventsCallback,
        setPassengerData,
        setPassengerLinkedCheckinData,
        setPassengerOmmit,
        setPassengerLinkedCheckinOmmit,
        // uploadPassengerImage,
        setNewsletter,
        setTravelType,
        setEntryTime,
        setArrivalMode,
        setEmail,
        setPhone,
        setComment,
        setSign,
        setExtraData,
        validate,
        validateAt,
        errors,
        resetErrors,
        sessionId,
        getBalance,
        getBalancePaymentUrl,
        setCrossUpSellingItems,
        isMailAllowed,
        refreshStatus,
        nights: nightsCount()
    };

    return <CheckInContext.Provider value={value}>
        {children}
    </CheckInContext.Provider>
}

const useCheckIn = () => {
    return useContext(CheckInContext);
};

export { CheckInProvider, useCheckIn, CheckInContext }